/**
 * useResumeVisit — single source of truth for the "Resume completed
 * visit" flow.
 *
 * The doctor (or a sitting-assigned nurse) clicks "Resume Visit" on a
 * procedure chip whose parent visit is COMPLETED + same-day. This hook
 * orchestrates:
 *
 *   1. Conflict-check: does the caller already have ANOTHER visit
 *      currently IN_PROGRESS? If yes, prompt with the 3-option
 *      ResumeConflictDialog (Park / Finish current / Cancel).
 *   2. Backend call: PUT /api/organisation/visits/{id}/resume,
 *      optionally passing park_current_visit_id for the atomic park
 *      step.
 *   3. Optimistic patch: flip the resumed visit's local status to
 *      IN_PROGRESS so the UI stops showing the Resume CTA immediately.
 *   4. After-resume callback: typically navigate to the visit screen,
 *      or auto-continue the chip's original "Start Procedure" action.
 *
 * Consumers spread `dialogProps` onto <ResumeConflictDialog />.
 */
import { useCallback, useMemo, useState } from 'react';
import { resumeVisit as apiResumeVisit } from '../services/api/visitApi';
import useVisitStore from '../stores/visitStore';

const inProgressFor = (visits, memberId) => {
  if (!Array.isArray(visits) || !memberId) return null;
  // Mirror DoctorQueueView's "currentVisit = visibleInProgress[0]"
  // rule: filter to IN_PROGRESS owned by the viewer and not parked
  // away via action_pending_with_member_id pointing elsewhere.
  const candidates = visits.filter(
    (v) =>
      v?.status === 'IN_PROGRESS' &&
      (v.doctor_member_id === memberId || v.viewer_role === 'DOCTOR') &&
      // If the visit's action-pending overlay points to someone else,
      // the doctor has already handed it off — don't treat as
      // conflicting.
      (!v.action_pending_with_member_id ||
        v.action_pending_with_member_id === memberId)
  );
  return candidates[0] || null;
};

export default function useResumeVisit({ viewerMemberId } = {}) {
  const visits = useVisitStore((s) => s.visits);
  const dailyVisits = useVisitStore((s) => s.dailyVisits);
  const patchVisitFields = useVisitStore((s) => s.patchVisitFields);

  // ── Dialog state ───────────────────────────────────────────────────
  const [dialogState, setDialogState] = useState({
    open: false,
    currentVisit: null,
    targetVisit: null,
    afterResume: null,
    inFlight: false,
    error: null,
  });

  const closeDialog = useCallback(
    () =>
      setDialogState({
        open: false,
        currentVisit: null,
        targetVisit: null,
        afterResume: null,
        inFlight: false,
        error: null,
      }),
    []
  );

  // ── Core resume call ───────────────────────────────────────────────
  const doResume = useCallback(
    async (targetVisitId, { parkCurrentVisitId, afterResume } = {}) => {
      const updated = await apiResumeVisit(targetVisitId, {
        parkCurrentVisitId,
      });
      // Optimistic local patch — the WS VISIT_STATUS_CHANGED event
      // will reconcile shortly, but flipping the local copy now stops
      // the chip from showing the Resume CTA mid-flight.
      patchVisitFields(targetVisitId, { status: 'IN_PROGRESS' });
      if (parkCurrentVisitId) {
        patchVisitFields(parkCurrentVisitId, { status: 'QUEUE' });
      }
      if (typeof afterResume === 'function') {
        try {
          await afterResume(updated);
        } catch (cbErr) {
          // afterResume failures shouldn't reverse the resume — log
          // and surface separately if needed.
          console.warn('[useResumeVisit] afterResume callback failed:', cbErr);
        }
      }
      return updated;
    },
    [patchVisitFields]
  );

  // ── Entry point: request a resume ──────────────────────────────────
  const requestResume = useCallback(
    async (targetVisit, { afterResume } = {}) => {
      if (!targetVisit || !targetVisit.visit_id) {
        console.warn('[useResumeVisit] requestResume: missing targetVisit');
        return;
      }

      // Find caller's current IN_PROGRESS visit (if any). Search both
      // visits lists since callers may be on different screens.
      const pool = (visits && visits.length) ? visits : dailyVisits;
      const current = inProgressFor(pool, viewerMemberId);

      // If no conflict OR the target IS the current visit, just call
      // resume directly. (The latter shouldn't happen since IN_PROGRESS
      // visits aren't resumable, but be defensive.)
      if (!current || current.visit_id === targetVisit.visit_id) {
        try {
          await doResume(targetVisit.visit_id, { afterResume });
        } catch (err) {
          // Caller surfaces toast via thrown error.
          throw err;
        }
        return;
      }

      // Conflict — open the dialog and let the user choose.
      setDialogState({
        open: true,
        currentVisit: current,
        targetVisit,
        afterResume,
        inFlight: false,
        error: null,
      });
    },
    [visits, dailyVisits, viewerMemberId, doResume]
  );

  // ── Dialog button handlers ─────────────────────────────────────────
  const onPark = useCallback(async () => {
    if (!dialogState.targetVisit || !dialogState.currentVisit) return;
    setDialogState((s) => ({ ...s, inFlight: true, error: null }));
    try {
      await doResume(dialogState.targetVisit.visit_id, {
        parkCurrentVisitId: dialogState.currentVisit.visit_id,
        afterResume: dialogState.afterResume,
      });
      closeDialog();
    } catch (err) {
      const msg =
        err?.message ||
        err?.detail ||
        'Could not resume the visit. Your current visit is unchanged — please retry.';
      setDialogState((s) => ({ ...s, inFlight: false, error: msg }));
    }
  }, [dialogState, doResume, closeDialog]);

  const onFinishCurrent = useCallback(() => {
    // No state change — just close. Doctor stays on current visit,
    // finishes it normally, then re-clicks Resume on the target.
    closeDialog();
  }, [closeDialog]);

  const onCancel = useCallback(() => {
    // No state change. Pure dismiss.
    closeDialog();
  }, [closeDialog]);

  // ── Props bag for <ResumeConflictDialog /> ─────────────────────────
  const dialogProps = useMemo(
    () => ({
      open: dialogState.open,
      currentVisit: dialogState.currentVisit,
      targetVisit: dialogState.targetVisit,
      inFlight: dialogState.inFlight,
      error: dialogState.error,
      onPark,
      onFinishCurrent,
      onCancel,
    }),
    [dialogState, onPark, onFinishCurrent, onCancel]
  );

  return {
    requestResume,
    dialogProps,
  };
}
